import React from "react";
import { Link } from "react-router-dom";
import {
  FileSignature,
  CheckCircle2,
  Truck,
  ClipboardCheck,
  ArrowUpRight,
} from "lucide-react";
import { PageHero, GoldButton, GhostGoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const SIGNING_IMG =
  "https://images.pexels.com/photos/8815849/pexels-photo-8815849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PACKAGES = [
  "Refinance packages",
  "Purchase & seller packages",
  "Reverse mortgage packages",
  "HELOC and 2nd mortgage signings",
  "Loan modification packages",
  "Deed-in-lieu and assumption packages",
  "Application packages & e-signings",
  "Trust deliveries and structured signings",
  "Marine / yacht / vessel title transfers & Coast Guard documentation",
  "Senior living transition & healthcare directive bedside visits",
  "Funeral home estate documentation & asset transfers",
  "Corporate filings, joint ventures & worldwide RON",
];

const STANDARDS = [
  {
    icon: ClipboardCheck,
    title: "Pre-Signing Document Audit",
    body: "Every loan packet is reviewed page-by-page before the signer sits down. Tabs are placed, required IDs are flagged, and discrepancies are escalated to the title officer before — not after — the appointment.",
  },
  {
    icon: CheckCircle2,
    title: "Error-Free Execution",
    body: "Zero cross-outs. Zero missed signatures, initials, or notarial certificates. We sign in black ink, print legibly, and verify that every required acknowledgement, jurat, and rider is complete.",
  },
  {
    icon: Truck,
    title: "Prompt Drop-Offs",
    body: "Same-day scans and tracked shipping per your closing instructions. We provide a complete confirmation — package label, drop location, time, and tracking number — before we leave the drop point.",
  },
];

export default function SigningAgentPage() {
  return (
    <>
      <PageHero
        eyebrow="For Title, Escrow & Mortgage Professionals"
        title="Loan signings closed cleanly the first time. No cross-outs. No callbacks."
        lead="Certified loan signing agent services for real estate title companies, mortgage brokers, and escrow officers. Specialized in managing 100+ page loan packets, closing disclosures, and deed packages with surgical attention to detail."
        backdrop={IMG.realEstateSigning}
        backdropOpacity={0.6}
        bright
      >
        <div className="flex flex-wrap gap-4">
          <GoldButton
            as={Link}
            to="/contact"
            data-testid="signing-agent-book-btn"
          >
            Schedule a Signing <ArrowUpRight className="w-4 h-4" />
          </GoldButton>
          <GhostGoldButton
            as={Link}
            to="/pricing"
            data-testid="signing-agent-pricing-btn"
          >
            View Loan Signing Rates
          </GhostGoldButton>
        </div>
      </PageHero>

      {/* Intro split */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionEyebrow className="mb-4">The Standard</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              Built for the closing table.
            </h2>
            <div className="mt-8 space-y-6 text-slate-300 leading-relaxed">
              <p>
                Real estate transactions don't tolerate sloppy paperwork. A
                single missed initial, an out-of-place date, or a cross-out on
                the Closing Disclosure can stall funding, trigger a re-draw,
                or — at worst — sink the deal. We treat every packet like the
                file is going to be audited tomorrow.
              </p>
              <p>
                We are comfortable with refinance, purchase, reverse mortgage,
                HELOC, application, and deed packages. We confirm signer
                identity, witness witness-required pages, and return clean,
                fully-executed documents ready to fund.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div
              className="relative border border-white/10 aspect-[4/5] bg-cover bg-center"
              style={{ backgroundImage: `url(${SIGNING_IMG})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#142339] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-[#142339]/80 backdrop-blur-sm">
                <div className="eyebrow mb-2">Field-Tested</div>
                <p className="font-serif-display text-xl text-white">
                  100+ page loan packets handled with zero cross-outs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10">
        <FadedBackdrop src={IMG.signingTable} opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Operating Standards</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Error-free execution & prompt drop-offs.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {STANDARDS.map((s, i) => (
              <div
                key={i}
                data-testid={`signing-standard-${i}`}
                className="bg-[#1A2B45] border border-white/10 p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor CTA strip */}
      <section className="border-b border-white/10 bg-[#1A2B45]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow mb-2">For Title, Escrow & Loan Pros</div>
            <p className="font-serif-display text-2xl sm:text-3xl text-white leading-snug">
              Add OGO Notary to your vendor list and close cleaner — one short form.
            </p>
          </div>
          <GoldButton
            as={Link}
            to="/vendor"
            data-testid="vendor-cta-signing"
          >
            Add Us to Your Vendor List <ArrowUpRight className="w-4 h-4" />
          </GoldButton>
        </div>
      </section>

      {/* Packages list */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow className="mb-4">Packages We Handle</SectionEyebrow>
            <h2 className="font-serif-display text-4xl text-white leading-tight">
              Specialized expertise across the full closing spectrum.
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              From a clean purchase signing to a complex reverse mortgage with
              multiple riders, we come prepared, properly tabbed, and ready to
              run the room.
            </p>
            <GoldButton
              as={Link}
              to="/contact"
              className="mt-8"
              data-testid="signing-agent-contact-btn"
            >
              Add Us to Your Vendor List <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {PACKAGES.map((p, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 border border-white/10 px-5 py-4 bg-[#1A2B45]"
                >
                  <FileSignature className="w-4 h-4 text-[#D4AF37] shrink-0" />
                  <span className="text-slate-200 text-sm">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
